import {useQueryClient} from '@tanstack/react-query';
import {useDeleteStep} from '../../../core/repositories/mypageEntryManagementsRepository';
import {invalidateEntryManagementCaches} from './cacheInvalidationHelpers';

export type DeletionParams = {
  readonly entryManagementId: number;
  readonly stepId: number;
};

export type DeletionCallbacks = {
  readonly onSuccess: (options?: {googleSyncFailed?: boolean}) => void;
  readonly onError: () => void;
};

/**
 * ステップ削除アクションを管理するカスタムフック
 *
 * ステップ削除専用API (DELETE /users/me/entry-managements/steps/{stepId}) を使用
 */
export const useProgressStepDeletionActions = () => {
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteStep();

  /**
   * 削除処理の実行
   *
   * @param params - 削除対象の情報
   * @param callbacks - 成功・エラー時のコールバック
   */
  const executeStepDeletion = async (
    params: DeletionParams,
    callbacks: DeletionCallbacks,
  ): Promise<void> => {
    try {
      // DELETE API でステップを削除
      const result = await deleteMutation.mutateAsync({
        stepId: params.stepId,
      });

      if (result.ok) {
        await invalidateEntryManagementCaches(
          queryClient,
          params.entryManagementId,
        );
        callbacks.onSuccess({
          googleSyncFailed: result.value.googleSyncResult?.success === false,
        });
      } else {
        callbacks.onError();
      }
    } catch (_error) {
      callbacks.onError();
    }
  };

  return {
    executeStepDeletion,
    isLoading: deleteMutation.isLoading,
    error: deleteMutation.error,
  } as const;
};
